import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import apiClient from '../utils/api';

const PaymentSuccess = () => {
    const { id: invoiceId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = searchParams.get('session_id');
    const [status, setStatus] = useState('processing'); // processing, success, error

    useEffect(() => {
        const verifyPayment = async () => {
            if (!sessionId) {
                setStatus('error');
                return;
            }

            try {
                // Poll for status
                const response = await apiClient.get(`/payments/status/${sessionId}`);
                if (response.payment_status === 'paid') {
                    setStatus('success');
                } else {
                    setStatus('processing');
                    // Retry after 2 seconds
                    setTimeout(verifyPayment, 2000);
                }
            } catch (error) {
                console.error('Error verifying payment:', error);
                setStatus('error');
            }
        };

        verifyPayment();
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                {status === 'processing' && (
                    <>
                        <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment...</h1>
                        <p className="text-gray-600">Please wait while we confirm your payment with Stripe.</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
                        <p className="text-gray-600 mb-8">Thank you for your payment. Your invoice has been updated.</p>
                        <button
                            onClick={() => navigate(`/client-portal`)}
                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Return to Portal
                        </button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl font-bold">!</div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Status Unknown</h1>
                        <p className="text-gray-600 mb-8">We couldn't verify your payment status immediately. It might take a few minutes to update.</p>
                        <button
                            onClick={() => navigate(`/client-portal`)}
                            className="w-full px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                        >
                            Go Back
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentSuccess;
